/**
 * Route guard for protected route GROUPS (R-18.179) — wraps the authenticated shell
 * in routes.tsx, not individual leaf routes.
 *
 * The redirect is DECLARATIVE in render via `<Navigate>` (R-18.181): redirecting
 * inside a `useEffect` would render `children` once before the effect fires, flashing
 * protected content. While the startup `/auth/me` probe is in flight we render a
 * spinner so the guard does not bounce to /login before the session is known.
 *
 * An unauthenticated user is sent to `/login` carrying `state.from` (R-18.180) so the
 * login screen can return them to the page they originally requested.
 */

import { Spin } from 'antd';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '@/shared/auth/AuthProvider';

export function ProtectedRoute(): React.JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <Spin fullscreen />;
  }

  if (!isAuthenticated) {
    // R-18.180/R-18.181 — declarative redirect; `replace` keeps the protected URL out
    // of history, `state.from` lets the login screen redirect back after success.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
